import { useEffect, useRef, type ReactNode } from 'react';
import { Bot, X } from 'lucide-react';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Optional rich content rendered under an assistant bubble (e.g. product cards). */
  extra?: ReactNode;
}

interface ChatShellProps {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: (open: boolean) => void;
  messages: ChatMessage[];
  loading?: boolean;
  /** A notice pinned above the messages (e.g. "AI offline — data lookups still work"). */
  banner?: ReactNode;
  /** Shown centered when there are no messages yet. */
  emptyHint?: ReactNode;
  /** The input area — differs per bot (free text vs crop picker). */
  composer: ReactNode;
  fabAriaLabel?: string;
}

/**
 * Shared floating chat modal (FAB → panel) used by BOTH the internal assistant and the public crop
 * advisor, so they look identical ("same modal"). Self-contained Tailwind (no app theme tokens) so it
 * renders consistently in the authed shell (light/dark) and on the public marketing site (light).
 */
export function ChatShell({ title, subtitle, open, onToggle, messages, loading, banner, emptyHint, composer, fabAriaLabel }: ChatShellProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, open]);

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onToggle(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onToggle]);

  if (!open) {
    return (
      <button
        type="button"
        aria-label={fabAriaLabel ?? 'Open assistant'}
        onClick={() => onToggle(true)}
        className="gb-assistant-floating fixed bottom-6 right-6 z-[1000] grid size-14 place-items-center rounded-full bg-brand-600 text-white shadow-[0_6px_18px_rgba(0,0,0,0.28)] transition hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <Bot size={24} />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label={title}
      className="gb-assistant-floating fixed bottom-6 right-6 z-[1000] flex h-[540px] max-h-[calc(100vh-96px)] w-[380px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-[0_16px_48px_rgba(0,0,0,0.32)] dark:bg-[#1a201a]"
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-line bg-brand-600 px-4 py-3 text-white">
        <Bot size={18} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="truncate type-body-strong leading-tight">{title}</div>
          {subtitle && <div className="truncate text-2xs leading-tight text-white/80">{subtitle}</div>}
        </div>
        <button type="button" aria-label="Close" onClick={() => onToggle(false)} className="grid size-7 place-items-center rounded-md text-white/90 transition hover:bg-white/15">
          <X size={16} />
        </button>
      </div>

      {banner}

      {/* Messages */}
      <div ref={bodyRef} className="flex-1 space-y-2 overflow-y-auto bg-surface-2 p-3 dark:bg-black/20">
        {messages.length === 0 && emptyHint && (
          <div className="mt-8 px-4 text-center text-sm text-subtle">{emptyHint}</div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex flex-col items-start'}>
            <div
              className={
                m.role === 'user'
                  ? 'max-w-[82%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-brand-600 px-3 py-2 text-sm leading-relaxed text-white'
                  : 'max-w-[86%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-line bg-surface px-3 py-2 text-sm leading-relaxed text-content'
              }
            >
              {m.content}
            </div>
            {m.role === 'assistant' && m.extra}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-1.5 px-1 py-1 text-subtle">
            <span className="size-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.2s]" />
            <span className="size-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.1s]" />
            <span className="size-1.5 animate-bounce rounded-full bg-current" />
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-line bg-surface p-2 dark:bg-[#1a201a]">{composer}</div>
    </div>
  );
}
